import React from 'react';
import AleaTable from '../../components/AleaTable/AleaTable';
import { ITableColumn } from '../../interfaces';
import { IAccount } from '../../interfaces/Account/IAccount.ts';
import {
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ICareHome } from '../../interfaces';
import formatLastLoginDate from '../../utils/formatLastLoginDate';

interface AppAccountsTableProps {
  appAccounts: IAccount[];
  isLoading: boolean;
  careHomes: ICareHome[];
  total: number;
  // Pagination props
  usePagination?: boolean;
  currentPage?: number;
  perPage?: number;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  onEditCaregiver?: (caregiver: IAccount) => void;
  onArchiveCaregiver?: (caregiver: IAccount) => void;
  onReactivateCaregiver?: (caregiver: IAccount) => void;
  isArchivedView: boolean;
  canManageCaregivers: boolean;
  showCareHomeColumn: boolean;
  activeActionRowId: string | null;
  setActiveActionRowId: (id: string | null) => void;
}

const AppAccountsTable: React.FC<AppAccountsTableProps> = ({
  appAccounts,
  isLoading,
  careHomes,
  total,
  // Pagination props
  usePagination = false,
  currentPage = 1,
  perPage = 10,
  onPageChange,
  onPerPageChange,
  onEditCaregiver,
  onArchiveCaregiver,
  onReactivateCaregiver,
  isArchivedView,
  canManageCaregivers,
  showCareHomeColumn,
  activeActionRowId,
  setActiveActionRowId,
}) => {
  const { t } = useTranslation();

  const archivedTextColor = isArchivedView ? '#C6C6C6' : undefined;

  // Helper function to get care home name by ID
  const getCareHomeName = (carehomeId: string | null | undefined): string => {
    if (!carehomeId) return '-';
    const careHome = careHomes.find((ch) => ch.id === carehomeId);
    return careHome?.name || '-';
  };

  const getColumns = (): ITableColumn<IAccount & { actions?: unknown }>[] => {
    const cols: ITableColumn<IAccount & { actions?: unknown }>[] = [
      {
        key: 'username',
        label: t('username'),
        sortable: true,
        render: (data: IAccount) => {
          return <Text color={archivedTextColor}>{data.username || '-'}</Text>;
        },
      },
      {
        key: 'last_login',
        label: t('lastLoggedIn'),
        sortable: true,
        render: (data: IAccount) => {
          return (
            <Text textAlign="left" color={archivedTextColor}>
              {formatLastLoginDate(data.last_login)}
            </Text>
          );
        },
      },
    ];

    if (showCareHomeColumn) {
      cols.splice(1, 0, {
        key: 'carehome_id',
        label: t('careHome'),
        sortable: true,
        render: (data: IAccount) => {
          return <Text color={archivedTextColor}>{getCareHomeName(data.carehome_id)}</Text>;
        },
      });
    }

    if (canManageCaregivers) {
      cols.push({
        key: 'actions' as keyof (IAccount & { actions?: unknown }),
        label: '',
        sortable: false,
        width: '35px',
        headerPadding: '0 0 0 10px',
        cellPadding: '0 0 0 10px',
        render: (data: IAccount) => {
          const rowIndex = appAccounts.findIndex((c) => c.id === data.id);
          const isStriped = rowIndex % 2 === 1; // 0-based: 0,2,... = white; 1,3,... = grey
          const baseBg = isStriped ? 'white' : '#F3F2F2';
          const isActive = activeActionRowId === data.id;
          const menuBg = isActive ? 'alea.500' : baseBg;
          const iconColor = isActive ? 'white' : '#6E6E6E';

          return (
            <Flex justifyContent="flex-end">
              <Menu
                isOpen={activeActionRowId === data.id}
                onOpen={() => setActiveActionRowId(data.id)}
                onClose={() => setActiveActionRowId(null)}
                placement="bottom-end"
                autoSelect={false}
              >
                <MenuButton
                  as={Box}
                  w="25px"
                  h="50px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  cursor="pointer"
                  bg={menuBg}
                >
                  <IconButton
                    aria-label="more"
                    variant="ghost"
                    color={iconColor}
                    size="xs"
                    w="25px"
                    h="50px"
                    cursor="pointer"
                    _hover={{ background: 'transparent' }}
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="6"
                        height="24"
                        viewBox="0 0 6 24"
                        fill="none"
                      >
                        <circle
                          cx="3"
                          cy="18"
                          r="2"
                          transform="rotate(-90 3 18)"
                          style={{ fill: iconColor }}
                        />
                        <circle
                          cx="3"
                          cy="12"
                          r="2"
                          transform="rotate(-90 3 12)"
                          style={{ fill: iconColor }}
                        />
                        <circle
                          cx="3"
                          cy="6"
                          r="2"
                          transform="rotate(-90 3 6)"
                          style={{ fill: iconColor }}
                        />
                      </svg>
                    }
                  />
                </MenuButton>
                <MenuList
                  borderRadius="20px"
                  border="1px solid #DCDCDC"
                  boxShadow="0px 8px 24px rgba(0,0,0,0.15)"
                  p="0"
                  zIndex={1502}
                  overflow="hidden"
                  bg="white"
                  motionProps={{
                    variants: {
                      enter: { opacity: 1, scale: 1 },
                      exit: {
                        opacity: 0,
                        scale: 1,
                      },
                    },
                  }}
                >
                {isArchivedView ? (
                  <MenuItem
                    onClick={() => {
                      onReactivateCaregiver?.(data);
                      setActiveActionRowId(null);
                    }}
                    fontSize="18px"
                    h="52px"
                  >
                    {t('reactivate')}
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem
                      onClick={() => {
                        onEditCaregiver?.(data);
                        setActiveActionRowId(null);
                      }}
                      fontSize="18px"
                      h="52px"
                      borderBottom="1px solid #DCDCDC"
                    >
                      {t('edit')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        onArchiveCaregiver?.(data);
                        setActiveActionRowId(null);
                      }}
                      fontSize="18px"
                      h="52px"
                      color="invalid.500"
                    >
                      {t('archive')}
                    </MenuItem>
                  </>
                )}
                </MenuList>
              </Menu>
            </Flex>
          );
        },
      });
    }

    return cols;
  };

  const columns = getColumns();

  return (
    <AleaTable<IAccount & Record<string, unknown>>
      data={appAccounts as (IAccount & Record<string, unknown>)[]}
      columns={columns}
      isLoading={isLoading}
      total={total}
      noRecordsText={t('general.noResults')}
      usePagination={usePagination}
      currentPage={currentPage}
      perPage={perPage}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
      activeRowId={activeActionRowId}
      lastColumnColSpan={1}
    />
  );
};

export default AppAccountsTable;


